import { useCallback, useEffect, useMemo, useState } from "react";
import { RestaurantList } from "../../models/IRestaurantDetail";
import { getRestaurantById, getRestaurantList } from "../../services/restaurant";
import { checkHasSalonOrderRole } from "../../services/checkRole";
import { getUserToken, isSuperAdmin } from "../../utils/Util";
import { DineInEventName, IDineInBill, IDineInEventPayload, IDineInFloor, IDineInFloorTable } from "../models/IDineIn";
import {
  dineInErrorCode, dineInErrorDescription, getDineInBillByTable, getDineInFloor,
  openDineInSession, releaseDineInSession,
} from "../services/dineInService";
import { useDineInHub } from "../hooks/useDineInHub";
import DineInBillPanel from "../components/DineInBillPanel";
import AddOrderLinesModal from "../components/AddOrderLinesModal";
import PayBillModal from "../components/PayBillModal";
import TableHistoryModal from "../components/TableHistoryModal";
import RevenueReportTab from "../components/RevenueReportTab";
import WaitersTab from "../components/WaitersTab";
import PhoneOrderTab from "../components/PhoneOrderTab";
import AddTableModal from "../components/AddTableModal";
import { getActiveRestaurantId } from "../../utils/activeRestaurant";
import SalonHistoryTab from "../components/SalonHistoryTab";

/** Restoran kullanıcısının bu sekmedeki restoranı (çok restoranlıda Dashboard'da seçilen). */
function ownRestaurantId(): string {
  return getActiveRestaurantId();
}

type DineInTab = "floor" | "history" | "phone" | "revenue" | "waiters";

const TABS: { key: DineInTab; label: string; icon: string; waiter: boolean }[] = [
  { key: "floor",   label: "Salon",       icon: "bi-grid-3x3-gap-fill", waiter: true },
  { key: "history", label: "Geçmiş",      icon: "bi-clock-history",     waiter: true },
  { key: "phone",   label: "Telefon Siparişi", icon: "bi-telephone-inbound-fill", waiter: false },
  { key: "revenue", label: "Ciro Raporu", icon: "bi-cash-stack",        waiter: false },
  { key: "waiters", label: "Garsonlar",   icon: "bi-person-badge-fill", waiter: false },
];

function money(n: number, symbol?: string): string {
  return `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${symbol ? ` ${symbol}` : ""}`;
}

function elapsed(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "az önce";
  if (mins < 60) return `${mins} dk`;
  const h = Math.floor(mins / 60);
  return `${h} sa ${mins % 60} dk`;
}

const HUB_STATE_META: Record<string, { label: string; cls: string }> = {
  connecting:   { label: "Bağlanıyor...",      cls: "dinein-live--pending" },
  connected:    { label: "Canlı",              cls: "dinein-live--on" },
  unauthorized: { label: "Canlı bağlantı yok", cls: "dinein-live--off" },
  error:        { label: "Bağlantı kesildi",   cls: "dinein-live--off" },
  idle:         { label: "—",                  cls: "dinein-live--off" },
};

interface DineInManagementPageProps {
  /**
   * "own": navbardaki Salon Siparişi bağlantısı — her zaman kullanıcının bağlı olduğu restoran
   * (SuperAdmin için de). Verilmezse SuperAdmin menüsü davranışı: SuperAdmin tüm restoranları seçer.
   *
   * "waiter": garson ekranı (/garson). Restoran garson girişinde dönen bağdan gelir; yalnız
   * Salon ve Geçmiş sekmeleri vardır, masa ekleme gibi yönetici işlemleri gösterilmez.
   */
  mode?: "own" | "waiter";
}

export default function DineInManagementPage({ mode }: DineInManagementPageProps) {
  const [restaurantList, setRestaurantList] = useState<RestaurantList>();
  const [restaurantId, setRestaurantId] = useState("");
  const [tab, setTab] = useState<DineInTab>("floor");

  const [floor, setFloor] = useState<IDineInFloor | null>(null);
  const [floorLoading, setFloorLoading] = useState(false);
  const [floorError, setFloorError] = useState("");

  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [bill, setBill] = useState<IDineInBill | null>(null);
  const [billLoading, setBillLoading] = useState(false);
  const [billError, setBillError] = useState("");

  const [busyTableId, setBusyTableId] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [historyTable, setHistoryTable] = useState<IDineInFloorTable | null>(null);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [showAddTable, setShowAddTable] = useState(false);

  const token = getUserToken()?.token ?? null;

  // Mobilde hesap paneli alttan açılan tam ekran sheet olarak gelir (parmakla kolay erişim)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // SuperAdmin restoran seçer; diğer kullanıcılar salon rolü varsa kendi restoranına kilitlenir.
  const superAdmin = useMemo(() => isSuperAdmin(), []);
  const waiter = mode === "waiter";
  // Restoran seçici yalnız SuperAdmin menüsünden girilince; aksi halde kendi restoranına kilitli.
  const canPickRestaurant = superAdmin && !mode;
  const visibleTabs = TABS.filter((t) => !waiter || t.waiter);
  const [access, setAccess] = useState<"checking" | "granted" | "denied" | "noRestaurant">(
    canPickRestaurant ? "granted" : "checking"
  );
  const [ownRestaurantName, setOwnRestaurantName] = useState("");

  useEffect(() => {
    if (canPickRestaurant) {
      getRestaurantList().then((res) => { if (res.data) setRestaurantList(res.data); });
      return;
    }
    let cancelled = false;
    // Salon uçları (18 Eki'den beri) yalnız SalonSiparisUserRole + SuperAdmin kabul eder;
    // garson da yönetici de bu rolü taşımalı. Rol + restoran bağı sunucuda yine doğrulanır.
    checkHasSalonOrderRole(token).then((ok) => {
      if (cancelled) return;
      if (!ok) { setAccess("denied"); return; }
      const own = ownRestaurantId();
      if (!own) { setAccess("noRestaurant"); return; }
      setRestaurantId(own);
      setAccess("granted");
      getRestaurantById(own)
        .then((res) => { if (!cancelled) setOwnRestaurantName(res.data?.restourantName ?? ""); })
        .catch(() => {});
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPickRestaurant]);

  const loadFloor = useCallback((id: string) => {
    setFloorLoading(true);
    setFloorError("");
    getDineInFloor(id)
      .then((res) => setFloor(res.data))
      .catch((err) => {
        setFloor(null);
        setFloorError(dineInErrorDescription(err) ?? "Salon görünümü yüklenemedi.");
      })
      .finally(() => setFloorLoading(false));
  }, []);

  const loadBill = useCallback((tableId: string) => {
    setBillLoading(true);
    setBillError("");
    getDineInBillByTable(tableId)
      .then((res) => setBill(res.data))
      .catch((err) => {
        setBill(null);
        const code = dineInErrorCode(err);
        setBillError(code === "NoOpenSession" ? "Bu masada açık hesap yok." : (dineInErrorDescription(err) ?? "Hesap yüklenemedi."));
      })
      .finally(() => setBillLoading(false));
  }, []);

  useEffect(() => {
    if (!restaurantId) { setFloor(null); setSelectedTableId(null); setBill(null); return; }
    loadFloor(restaurantId);
    setSelectedTableId(null);
    setBill(null);
  }, [restaurantId, loadFloor]);

  // Olaylar yalnız tazeler: bağlantı kopukken geçenler geri gelmez, bu yüzden /floor tekrar çağrılır.
  const hubState = useDineInHub({
    token,
    restaurantId: restaurantId || null,
    onEvent: useCallback((_name: DineInEventName, payload: IDineInEventPayload) => {
      if (!restaurantId || payload.restourantId !== restaurantId) return;
      loadFloor(restaurantId);
      setSelectedTableId((current) => {
        if (current && current === payload.tableId) loadBill(current);
        return current;
      });
    }, [restaurantId, loadFloor, loadBill]),
    // Bağlantı kopup geri gelince kaçan olaylar için salonu ve açık hesabı tazele
    onResync: useCallback(() => {
      if (!restaurantId) return;
      loadFloor(restaurantId);
      setSelectedTableId((current) => {
        if (current) loadBill(current);
        return current;
      });
    }, [restaurantId, loadFloor, loadBill]),
  });

  const tables = floor?.tables ?? [];
  const selectedTable = useMemo(
    () => tables.find((t) => t.tableId === selectedTableId) ?? null,
    [tables, selectedTableId]
  );

  const busyCount = tables.filter((t) => t.status === "Busy").length;
  const openTotal = tables.reduce((s, t) => s + (t.openSession?.totalAmount ?? 0), 0);
  const floorCurrency = tables.find((t) => t.openSession?.currencyDisplayText)?.openSession?.currencyDisplayText;

  const selectTable = (table: IDineInFloorTable) => {
    setSelectedTableId(table.tableId);
    setActionError("");
    setConfirmRelease(false);
    if (table.status === "Busy") loadBill(table.tableId);
    else { setBill(null); setBillError(""); }
  };

  const handleOpenTable = (table: IDineInFloorTable) => {
    setBusyTableId(table.tableId);
    setActionError("");
    openDineInSession(table.tableId)
      .then(() => { loadFloor(restaurantId); loadBill(table.tableId); setSelectedTableId(table.tableId); })
      .catch((err) => {
        const code = dineInErrorCode(err);
        setActionError(
          code === "TableAlreadyBusy" ? "Bu masa az önce başka bir cihazda açıldı. Salon görünümü yenilendi."
          : code === "TableRetired" ? "Bu masa kullanım dışı. Salon görünümü yenilendi."
          : code === "TableNotFound" ? "Masa bulunamadı. Salon görünümü yenilendi."
          : (dineInErrorDescription(err) ?? "Masa açılamadı.")
        );
        loadFloor(restaurantId);
      })
      .finally(() => setBusyTableId(null));
  };

  const handleRelease = () => {
    const code = bill?.session.orderCode ?? selectedTable?.openSession?.orderCode;
    if (!code) return;
    setBusyTableId(selectedTableId);
    setActionError("");
    releaseDineInSession(code)
      .then(() => {
        setConfirmRelease(false);
        setBill(null);
        loadFloor(restaurantId);
      })
      .catch((err) => {
        const c = dineInErrorCode(err);
        setActionError(
          c === "TableHasUnpaidLines" ? "Satırı olan masa ödemesiz boşaltılamaz. Ödeme alın ya da satırları tek tek silin."
          : c === "SessionNotOpen" ? "Bu oturum zaten kapanmış."
          : (dineInErrorDescription(err) ?? "Masa boşaltılamadı.")
        );
      })
      .finally(() => setBusyTableId(null));
  };

  const hubMeta = HUB_STATE_META[hubState] ?? HUB_STATE_META.idle;

  if (access !== "granted") {
    return (
      <div className="dinein-page">
        <div className="dinein-center dinein-center--tall">
          {access === "checking" ? (
            <span className="spinner-border" />
          ) : (
            <>
              <i className="bi bi-shield-lock"></i>
              <div>
                {access === "denied"
                  ? "Salon Siparişi ekranına erişim yetkiniz yok."
                  : "Hesabınıza bağlı bir restoran bulunamadı."}
              </div>
              <div className="dinein-small dinein-muted">Yetki için sistem yöneticisiyle iletişime geçin.</div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dinein-page">
      {/* Başlık */}
      <div className="dinein-header">
        <div className="dinein-header__left">
          <div className="dinein-header__icon"><i className="bi bi-shop-window"></i></div>
          <div>
            <h4 className="dinein-header__title">Salon Siparişi</h4>
            <p className="dinein-header__subtitle">
              {waiter
                ? "Masayı açın, siparişi girin, hesabı alın. Değişiklikler diğer tabletlere anında yansır."
                : "Restoranın kendi salonunda alınan siparişler — mutfacik.com siparişlerinden ayrıdır."}
            </p>
          </div>
        </div>
        {restaurantId && tab === "floor" && (
          <div className="dinein-header__actions">
            <span className={`dinein-live ${hubMeta.cls}`} title="Canlı güncelleme durumu">
              <span className="dinein-live__dot" />{hubMeta.label}
            </span>
            <button className="dinein-btn dinein-btn--ghost" onClick={() => loadFloor(restaurantId)} disabled={floorLoading}>
              <i className={`bi bi-arrow-clockwise${floorLoading ? " dinein-spin" : ""} me-1`}></i>Yenile
            </button>
          </div>
        )}
      </div>

      {/* Restoran seçici — yalnız SuperAdmin; diğerleri kendi restoranında */}
      {canPickRestaurant ? (
        <div className="dinein-restaurant">
          <label><i className="bi bi-building me-1"></i>Restoran</label>
          <select className="form-select form-select-lg" value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}>
            <option value="" disabled>Restoran seçiniz...</option>
            {restaurantList?.items.map((r) => (
              <option key={r.id} value={r.id}>{r.restourantName}</option>
            ))}
          </select>
        </div>
      ) : ownRestaurantName && (
        <div className="dinein-own-restaurant">
          <i className="bi bi-building"></i>
          <span>{ownRestaurantName}</span>
        </div>
      )}

      {!restaurantId ? (
        <div className="dinein-center dinein-center--tall">
          <i className="bi bi-shop"></i>
          <div>Salonu görmek için bir restoran seçin.</div>
        </div>
      ) : (
        <>
          {/* Sekmeler */}
          <div className="dinein-tabs" role="tablist">
            {visibleTabs.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
                className={`dinein-tab${tab === t.key ? " dinein-tab--active" : ""}`}
                onClick={() => setTab(t.key)}>
                <i className={`bi ${t.icon}`}></i>
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          {tab === "history" && <SalonHistoryTab restaurantId={restaurantId} tables={tables} />}
          {!waiter && tab === "phone" && <PhoneOrderTab restaurantId={restaurantId} />}
          {!waiter && tab === "revenue" && <RevenueReportTab restaurantId={restaurantId} />}
          {!waiter && tab === "waiters" && <WaitersTab restaurantId={restaurantId} />}

          {tab === "floor" && (
          <>
          {floorError && (
            <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{floorError}</span></div>
          )}
          {actionError && (
            <div className="dinein-alert dinein-alert--warn">
              <i className="bi bi-exclamation-triangle-fill"></i><span>{actionError}</span>
              <button type="button" onClick={() => setActionError("")}><i className="bi bi-x-lg"></i></button>
            </div>
          )}

          {/* Özet */}
          {floor && (
            <div className="dinein-summary">
              <div className="dinein-summary__item">
                <span>Masa</span><strong>{tables.length}</strong>
              </div>
              <div className="dinein-summary__item dinein-summary__item--busy">
                <span>Dolu</span><strong>{busyCount}</strong>
              </div>
              <div className="dinein-summary__item dinein-summary__item--free">
                <span>Boş</span><strong>{tables.length - busyCount}</strong>
              </div>
              <div className="dinein-summary__item dinein-summary__item--total">
                <span>Açık hesaplar</span><strong>{money(openTotal, floorCurrency)}</strong>
              </div>
            </div>
          )}

          <div className="dinein-layout">
            {/* Salon */}
            <div className="dinein-floor">
              {floorLoading && !floor ? (
                <div className="dinein-center dinein-center--tall"><span className="spinner-border" /></div>
              ) : tables.length === 0 ? (
                <div className="dinein-center dinein-center--tall">
                  <i className="bi bi-grid-3x3-gap"></i>
                  <div>Hizmette masa yok.</div>
                  {waiter ? (
                    <div className="dinein-small dinein-muted">Masa eklemek için restoran yöneticinize başvurun.</div>
                  ) : (
                    <button type="button" className="dinein-btn dinein-btn--primary" style={{ marginTop: 8 }}
                      onClick={() => setShowAddTable(true)}>
                      <i className="bi bi-plus-lg me-1"></i>İlk masayı ekle
                    </button>
                  )}
                </div>
              ) : (
                <div className="dinein-tables">
                  {tables.map((t) => {
                    const busy = t.status === "Busy";
                    const selected = t.tableId === selectedTableId;
                    return (
                      <button key={t.tableId} type="button"
                        className={`dinein-table${busy ? " dinein-table--busy" : ""}${selected ? " dinein-table--selected" : ""}`}
                        onClick={() => selectTable(t)}>
                        <div className="dinein-table__no">{t.tableNumber}</div>
                        {t.label && <div className="dinein-table__label">{t.label}</div>}
                        <div className="dinein-table__cap"><i className="bi bi-people-fill me-1"></i>{t.capacity}</div>
                        {busy && t.openSession ? (
                          <div className="dinein-table__session">
                            <span>{money(t.openSession.totalAmount, t.openSession.currencyDisplayText)}</span>
                            <span className="dinein-small">{t.openSession.lineCount} satır · {elapsed(t.openSession.openedAtUtc)}</span>
                          </div>
                        ) : (
                          <div className="dinein-table__free">Boş</div>
                        )}
                      </button>
                    );
                  })}
                  {!waiter && (
                    <button type="button" className="dinein-table dinein-table--add" onClick={() => setShowAddTable(true)}>
                      <i className="bi bi-plus-lg"></i>
                      <span>Masa Ekle</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Sağ panel — mobilde alttan açılan sheet */}
            {isMobile && selectedTable && (
              <div className="dinein-sheet-backdrop" onClick={() => setSelectedTableId(null)} />
            )}
            <div className={
              isMobile
                ? `dinein-sheet${selectedTable ? " dinein-sheet--open" : ""}`
                : "dinein-side"
            }>
              {isMobile && selectedTable && (
                <button type="button" className="dinein-sheet__handle" onClick={() => setSelectedTableId(null)} aria-label="Kapat">
                  <span />
                </button>
              )}
              {!selectedTable ? (
                !isMobile && (
                  <div className="dinein-center dinein-center--tall">
                    <i className="bi bi-hand-index-thumb"></i>
                    <div>İşlem için bir masa seçin.</div>
                  </div>
                )
              ) : (
                <>
                  <div className="dinein-side__head">
                    <div>
                      <div className="dinein-side__title">
                        Masa {selectedTable.tableNumber}
                        {selectedTable.label && <span className="dinein-muted"> — {selectedTable.label}</span>}
                      </div>
                      <div className="dinein-small dinein-muted">
                        {selectedTable.status === "Busy" && bill
                          ? <>Açan: {bill.session.openedByUserName ?? "—"} · {elapsed(bill.session.openedAtUtc)} önce açıldı</>
                          : `${selectedTable.capacity} kişilik`}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button className="dinein-icon-btn" title="Masa geçmişi" onClick={() => setHistoryTable(selectedTable)}>
                        <i className="bi bi-clock-history"></i>
                      </button>
                      {isMobile && (
                        <button className="dinein-icon-btn" title="Kapat" onClick={() => setSelectedTableId(null)}>
                          <i className="bi bi-x-lg"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  {selectedTable.status !== "Busy" ? (
                    <div className="dinein-side__empty">
                      <i className="bi bi-door-open"></i>
                      <div>Masa boş.</div>
                      <button className="dinein-btn dinein-btn--primary dinein-btn--block"
                        disabled={busyTableId === selectedTable.tableId}
                        onClick={() => handleOpenTable(selectedTable)}>
                        {busyTableId === selectedTable.tableId
                          ? <><span className="spinner-border spinner-border-sm me-1" />Açılıyor...</>
                          : <><i className="bi bi-play-fill me-1"></i>Masayı Aç</>}
                      </button>
                    </div>
                  ) : billLoading && !bill ? (
                    <div className="dinein-center dinein-center--tall"><span className="spinner-border" /></div>
                  ) : billError ? (
                    <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{billError}</span></div>
                  ) : bill ? (
                    <>
                      <DineInBillPanel
                        bill={bill}
                        onBillChanged={(b) => { setBill(b); loadFloor(restaurantId); }}
                        onNeedsRefresh={() => { loadBill(selectedTable.tableId); loadFloor(restaurantId); }}
                      />

                      {!bill.isReadOnly && (
                        <div className="dinein-side__actions">
                          <button className="dinein-btn dinein-btn--primary dinein-btn--block" onClick={() => setShowAdd(true)}>
                            <i className="bi bi-plus-lg me-1"></i>Ürün Ekle
                          </button>

                          {bill.lines.length > 0 ? (
                            <button className="dinein-btn dinein-btn--success dinein-btn--block" onClick={() => setShowPay(true)}>
                              <i className="bi bi-cash-coin me-1"></i>Ödeme Al
                            </button>
                          ) : confirmRelease ? (
                            <div className="dinein-confirm">
                              <span>Masa boşaltılsın mı?</span>
                              <div>
                                <button className="dinein-btn dinein-btn--ghost" onClick={() => setConfirmRelease(false)}>Hayır</button>
                                <button className="dinein-btn dinein-btn--danger" disabled={busyTableId === selectedTable.tableId} onClick={handleRelease}>
                                  {busyTableId === selectedTable.tableId
                                    ? <span className="spinner-border spinner-border-sm" />
                                    : "Evet, boşalt"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button className="dinein-btn dinein-btn--ghost dinein-btn--block" onClick={() => setConfirmRelease(true)}>
                              <i className="bi bi-x-circle me-1"></i>Masayı Boşalt
                            </button>
                          )}
                        </div>
                      )}

                      {bill.isReadOnly && (
                        <div className="dinein-alert dinein-alert--info">
                          <i className="bi bi-lock-fill"></i><span>Bu oturum kapanmış, salt okunur.</span>
                        </div>
                      )}
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
          </>
          )}
        </>
      )}

      {showAdd && bill && (
        <AddOrderLinesModal
          restaurantId={restaurantId}
          orderCode={bill.session.orderCode}
          lockedCurrencyId={bill.session.currencyId}
          currencySymbol={bill.session.currencyDisplayText}
          onClose={() => setShowAdd(false)}
          onAdded={(b) => { setBill(b); loadFloor(restaurantId); }}
        />
      )}

      {showPay && bill && (
        <PayBillModal
          bill={bill}
          onClose={() => setShowPay(false)}
          onBillRefreshed={(b) => setBill(b)}
          onPaid={() => {
            setShowPay(false);
            setBill(null);
            setSelectedTableId(null);
            loadFloor(restaurantId);
          }}
        />
      )}

      {!waiter && showAddTable && restaurantId && (
        <AddTableModal
          restaurantId={restaurantId}
          existingTables={tables}
          onClose={() => setShowAddTable(false)}
          onAdded={() => loadFloor(restaurantId)}
        />
      )}

      {historyTable && (
        <TableHistoryModal
          tableId={historyTable.tableId}
          tableNumber={historyTable.tableNumber}
          tableLabel={historyTable.label}
          onClose={() => setHistoryTable(null)}
        />
      )}
    </div>
  );
}
